import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AccountService,
  AlertService,
  AuthenticationService,
  NotificationService,
  UserService,
  FunnelService,
  PusherService,
  CallLogsService,
  SmsLogsService,
  MailLogsService,
  CampaignService,
} from '../_services';
import { Account, EventStatus, Notification, Pager, User, NotificationType, LogType } from '../_models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { config } from '../../config/config';
import { CrudPagedListComponent } from '../_directives';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent extends CrudPagedListComponent implements OnInit, OnDestroy {
  currentUser: User;
  notifications: Notification[];
  groupedNotifications: { label: string; notifications: Notification[] }[] = [];
  selectedNotification: Notification;
  selectedNotificationEventStatus: string = '';
  selectedNotificationEventName: string = '';
  NotificationStatus = EventStatus;
  markAllAsReadLoading: boolean = false;
  @ViewChild('notificationDropdown', { static: false }) notificationDropdown: ElementRef;

  protected _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private notificationService: NotificationService,
    private authService: AuthenticationService,
    private modalService: NgxSmartModalService,
    protected alertService: AlertService,
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    private userService: UserService,
    private accountService: AccountService,
    private funnelService: FunnelService,
    private pusherService: PusherService,
    private callLogsService: CallLogsService,
    private smsLogsService: SmsLogsService,
    private mailLogsService: MailLogsService,
    private campaignService: CampaignService
  ) {
    super(router, location, route, notificationService, alertService);
  }

  get pendingOrUnreadNotifications() {
    return (this.notifications || []).filter(
      (notification: Notification) => notification.event_status === EventStatus.IN_PROGRESS || !notification.is_read
    );
  }

  processNotification(data: any) {
    if (this.isLoggedIn() && data) {
      this.notificationService.setNotification(data);

      // Trigger bell shake animation for new notification
      this.triggerBellShake();

      if (this.notificationDropdown && this.notificationDropdown.nativeElement) {
        if (!this.notificationDropdown.nativeElement.classList.contains('show')) {
          const notificationsEl = this.notificationDropdown.nativeElement.querySelector('#notifications');
          notificationsEl && notificationsEl.click();
        }
      }

      if (data.type === NotificationType.PORTAL_CREATION && data.event_status === EventStatus.HAS_ERROR) {
        let campaign = this.campaignService.getCurrentCampaign();
        campaign.portal_creation_failed = true;
        this.campaignService.currentCampaignUpdated(campaign);
      }
    }
  }

  isActiveTab() {
    return document && document.visibilityState === 'visible';
  }

  handleAutoDownloadNotification(data: any) {
    this.processNotification(data);
    if (
      !this.currentUser ||
      !this.currentUser.account ||
      data.account !== this.currentUser.account.id ||
      !this.isActiveTab()
    ) {
      return;
    }
    const payload = data.payload;
    if (payload && payload.is_completed && payload.url) {
      const downloadLink = document.createElement('a');
      downloadLink.setAttribute('href', payload.url);
      downloadLink.style.visibility = 'hidden';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }

  triggerBellShake() {
    if (this.notificationDropdown && this.notificationDropdown.nativeElement) {
      const bellIcon = this.notificationDropdown.nativeElement.querySelector('.notification-icon');
      if (bellIcon) {
        bellIcon.classList.add('shake');
        // Remove the class after animation completes
        setTimeout(() => {
          bellIcon.classList.remove('shake');
        }, 600);
      }
    }
  }

  ngOnInit() {
    let _this = this;

    this.notificationService.notifications$
      .pipe(takeUntil(this._destroy$))
      .subscribe((notifications: Notification[]) => {
        this.notifications = notifications;
        this.groupNotificationsByDay();
        this.loading = false;
      });

    this.accountService.selectedAccount$.pipe(takeUntil(this._destroy$)).subscribe((account: Account) => {
      if (this.currentUser) {
        this.currentUser.account = account;
      }
      this.notificationService.clearNotifications();
      this.loadNotifications();
      this.loading = false;
    });

    if (this.isLoggedIn()) {
      this.userService.getCurrent().subscribe(
        (user: User) => {
          this.currentUser = user;
          this.pusherService.initialize();
        },
        (error) => {}
      );
    }

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.CRM_SYNCING], (data: any) => {
        _this.processNotification(data);
      });
      this.pusherService.channel.bind(NotificationType[NotificationType.PATH_PUBLISHING], (data: any) => {
        _this.processNotification(data);
      });
      this.pusherService.channel.bind(NotificationType[NotificationType.COPY_FUNNEL], (data: any) => {
        _this.processNotification(data);
        if (this.currentUser && this.currentUser.account && data.account == this.currentUser.account.id) {
          data = data.payload;
          if (data) {
            if (data.is_completed) {
              _this.funnelService.copiedFunnel.next(data.id);
            } else if (data.has_error) {
              _this.funnelService.copiedFunnel.error(data);
            }
          }
        }
      });
      this.pusherService.channel.bind(NotificationType[NotificationType.GENERATING_REPORT], (data: any) => {
        _this.handleAutoDownloadNotification(data);
      });
      this.pusherService.channel.bind(NotificationType[NotificationType.DOWNLOADING_LOGS], (data: any) => {
        _this.handleAutoDownloadNotification(data);
      });
      this.pusherService.channel.bind(NotificationType[NotificationType.REGEN_INVOICE], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.DEV_ENV_CREATION], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.CRM_DELETION], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.CUSTOM_DOMAIN_DELETION], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.COPY_AUDIENCE], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.PUBLISH_SURVEY_TEMPLATE], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.PORTAL_CREATION], (data: any) => {
        _this.processNotification(data);
      });

      this.pusherService.channel.bind(NotificationType[NotificationType.LOGS], (data: any) => {
        if (data.type === LogType.PhoneLogs) {
          this.callLogsService.updateLogs(data);
        } else if (data.type === LogType.SmsLogs) {
          this.smsLogsService.updateLogs(data);
        } else if (data.type === LogType.EmailLogs) {
          this.mailLogsService.updateLogs(data);
        }
      });
    }
  }

  loadNotifications() {
    this.notificationService
      .list({ page: 1, page_size: config.defaultPageSize })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          this.onPagerData(data);
          this.setPagerData(data);
          this.loading = false;
        },
        (error) => {
          this.handleError(error);
          this.onPagerError(error);
          this.loading = false;
        }
      );
  }

  isLoggedIn() {
    return this.authService.isLoggedIn();
  }

  notificationDetails(notification: Notification) {
    this.selectedNotification = notification;
    this.selectedNotificationEventStatus = EventStatus[notification.event_status];
    this.selectedNotificationEventStatus = this.selectedNotificationEventStatus.split('_').pop();
    this.selectedNotificationEventName = notification.event_name.replace('_', ' ');

    if (!notification.is_read) {
      this.selectedNotification.is_read = true;
      this.notificationService.setNotification(this.selectedNotification);
      this.notificationService.updateNotification(notification).subscribe();
    }

    this.modalService.getModal('notificationDetailsDialog').open();
  }

  markAllAsRead() {
    this.markAllAsReadLoading = true;
    this.notificationService.markAllRead().subscribe(
      () => {
        if (this.notifications) {
          this.notifications.forEach(notification => {
            notification.is_read = true;
          });
          this.notificationService.setInitialNotifications(this.notifications);
        }
        this.markAllAsReadLoading = false;
      },
      (error) => {
        this.handleError(error);
        this.markAllAsReadLoading = false;
      }
    );
  }

  groupNotificationsByDay() {
    const groups: { [key: string]: Notification[] } = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (this.notifications) {
      this.notifications.forEach((notification: Notification) => {
        const notificationDate = new Date(notification.updated_at);
        notificationDate.setHours(0, 0, 0, 0);

        let label = '';
        if (notificationDate.getTime() === today.getTime()) {
          label = 'TODAY';
        } else if (notificationDate.getTime() === yesterday.getTime()) {
          label = 'YESTERDAY';
        } else {
          label = notificationDate
            .toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
            .toUpperCase();
        }

        if (!groups[label]) {
          groups[label] = [];
        }
        groups[label].push(notification);
      });
    }

    this.groupedNotifications = Object.keys(groups).map((label) => {
      return {
        label: label,
        notifications: groups[label],
      };
    });
  }

  onScroll(event) {
    const scrollHeight = event.target.scrollHeight;
    const clientHeight = event.target.clientHeight;
    const scrollTop = event.target.scrollTop;
    const height = Math.ceil(clientHeight + scrollTop);
    if (height >= scrollHeight && this.page < this.pageCount) {
      this.loading = true;
      this.onPageChange({ page: this.page + 1, page_size: config.defaultPageSize });
    }
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }
}
